import React from 'react';
import styled from 'styled-components';
import { compose, withHandlers, withProps } from 'recompose';
import { RenderIf } from '../../features/common/organisms/index';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import i18next from 'i18next';

const enchance = compose(
  withHandlers({
    onPrevPage: ({ onPageChange, currentPage }) => () => {
      const nextPage = currentPage - 1 < 1 ? 1 : currentPage - 1;

      onPageChange(nextPage);
    },
    onNextPage: ({ onPageChange, currentPage, pageCount }) => () => {
      const nextPage = currentPage + 1 > pageCount ? pageCount : currentPage + 1;

      onPageChange(nextPage);
    },
    onPageClick: ({ onPageChange, currentPage }) => (id) => {
      if (id !== currentPage) {
        onPageChange(id);
      }
    },
  }),
  withProps(({ pageRange, currentPage, pageCount, restProps }) => {
    const [start, end] = getRange(pageRange, currentPage, pageCount);
    const hasNext = currentPage < pageCount;
    const hasPrev = currentPage > 1;
    const pages = [];

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return {
      pageRange,
      currentPage,
      pageCount,
      pages,
      ...restProps,
      hasNext,
      hasPrev,
    };
  })
);

const PaginationView = ({ currentPage, hasPrev, hasNext, onPrevPage, onNextPage, onPageClick, pages, pageCount }) => (
  <RenderIf cond={pageCount > 1} noText>
    <Container>
      {hasPrev && (
        <PrevButton onClick={onPrevPage}>
          <Arrow right />
          {i18next.t('cabinet.pagination.prev')}
        </PrevButton>
      )}
      <PageList>
        {pages.map((item) => (
          <Page active={currentPage === item} key={item} onClick={() => onPageClick(item)}>
            {item}
          </Page>
        ))}
      </PageList>
      {hasNext && (
        <NextButton onClick={onNextPage}>
          {i18next.t('cabinet.pagination.next')}
          <Arrow />
        </NextButton>
      )}
    </Container>
  </RenderIf>
);

export const Pagination = enchance(PaginationView);

const Arrow = styled.div`
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath fill='%23D5D5D5' fill-rule='nonzero' d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'/%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/svg%3E%0A");
  background-size: 24px 24px;
  background-repeat: no-repeat;
  background-position: center center;
  width: 24px;
  height: 24px;
  transform: ${(p) => p.right && 'rotate(180deg)'};
`;

const Container = styled.div`
  display: flex;
`;

const Button = styled.button`
  ${Mixins.transition('background-color color')}
  box-shadow: none;
  border: none;
  background: ${(p) => (p.active ? Colors.planoplan_blue : Colors.white)};
  height: 48px;
  font-size: 18px;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  color: ${(p) => (p.active ? Colors.white : Colors.coal)};
  padding-left: 20px;
  padding-right: 20px;
  line-height: 48px;
  cursor: ${(p) => (p.active ? 'auto' : 'pointer')};
  outline: none;

  &:hover {
    background-color: ${(p) => (p.active ? Colors.planoplan_blue : Colors.electric_blue)};
    color: ${Colors.white};
  }
`;

const Page = styled(Button)`
  &:not(:last-child) {
    margin-right: 2px;
  }
`;

const PageList = styled.div`
  display: flex;

  & ${Button}:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  & ${Button}:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`;

const NavButton = styled(Button)`
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NextButton = styled(NavButton)`
  padding-right: 8px;
  margin-left: 24px;
`;

const PrevButton = styled(NavButton)`
  padding-left: 8px;
  margin-right: 24px;
`;

function getRange(pageRange, currentPage, pageCount) {
  let start = currentPage - pageRange;
  let end = currentPage + pageRange;

  if (start < 1) {
    end -= start - 1;
  }

  if (end > pageCount) {
    start -= end - pageCount;
    end = pageCount;
  }

  if (start < 1) {
    start = 1;
  }

  return [start, end];
}

Pagination.defaultProps = {
  pageCount: 1,
  currentPage: 1,
  onPageChange: () => {},
  pageRange: 1,
};
